import React from 'react';
import { Navigate } from 'react-router-dom';
import { Box, Typography, Button } from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';
import { useRoleAccess } from '../hooks/useRoleAccess';
import { useNavigate } from 'react-router-dom';

interface RoleProtectedRouteProps {
  children: React.ReactNode;
  requiredRole: 'admin' | 'user' | 'any';
  redirectTo?: string;
}

/**
 * Component that protects routes based on user role
 * Shows an access denied message if user doesn't have required role
 */
const RoleProtectedRoute: React.FC<RoleProtectedRouteProps> = ({
  children,
  requiredRole,
  redirectTo
}) => {
  const { hasAccess, isSuperAdmin } = useRoleAccess();
  const navigate = useNavigate();
  const homeRoute = isSuperAdmin ? '/knowledge-base' : '/dashboard';

  // If redirect path is provided, redirect to that path
  if (redirectTo && !hasAccess(requiredRole)) {
    return <Navigate to={redirectTo} replace />;
  }

  // If user doesn't have access, show access denied page
  if (!hasAccess(requiredRole)) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="80vh"
        gap={3}
        className="bg-gray-50"
      >
        <LockIcon sx={{ fontSize: 80, color: 'error.main' }} />
        <Typography variant="h4" gutterBottom>
          Access Denied
        </Typography>
        <Typography variant="body1" color="textSecondary" textAlign="center" maxWidth={500}>
          You don't have permission to access this page. This page requires{' '}
          <strong>{requiredRole}</strong> role
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Please contact your administrator if you believe this is an error.
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate(homeRoute)}
          sx={{ mt: 2 }}
        >
          {isSuperAdmin ? 'Go to Knowledge Base' : 'Go to Dashboard'}
        </Button>
      </Box>
    );
  }

  // User has access, render children
  return <>{children}</>;
};

export default RoleProtectedRoute;
